import { PointColorHSL } from "@/core/types"
import { ColorSwatches } from "../Swatches"
import { linearGradient } from "@/utils/gradient"
import { HSVToRGB, RGBToHSV } from "@/core/utils/color-conversion"
import {
	PointColorHueMaxShift,
	PointColorLumMaxShift,
	PointColorSatMaxShift,
} from "@/core/hsl-point-color"

/* HSL */

const swatchesValueMapHSL: { [key in HSLColorSwatches]: number[] } = {
	red: [3, 100, 45],
	orange: [32, 94, 50],
	yellow: [63, 100, 45],
	green: [153, 100, 45],
	aqua: [183, 80, 50],
	blue: [213, 100, 50],
	purple: [273, 100, 61],
	magenta: [303, 100, 45],
}

export const HSLColors = [
	"red",
	"orange",
	"yellow",
	"green",
	"aqua",
	"blue",
	"purple",
	"magenta",
] as const

export type HSLColorSwatches = (typeof HSLColors)[number]

export function getHueBackground(
	color: ColorSwatches | [number, number, number],
	range: number = 30,
	minLum: number = 0,
) {
	if (typeof color === "string") {
		const hsl = swatchesValueMapHSL[color]

		return linearGradient([
			`hsl(${(hsl[0] - range) % 360}, ${hsl[1]}%, ${hsl[2]}%)`,
			`hsl(${(hsl[0] + range) % 360}, ${hsl[1]}%, ${hsl[2]}%)`,
		])
	} else {
		const [hue, sat, val] = color
		const valToDisplay = Math.max(val, minLum)
		const [r1, g1, b1] = HSVToRGB([(hue - range + 360) % 360, sat, valToDisplay])
		const [r2, g2, b2] = HSVToRGB([(hue + range + 360) % 360, sat, valToDisplay])

		return linearGradient([
			`rgb(${r1 * 255}, ${g1 * 255}, ${b1 * 255})`,
			`rgb(${r2 * 255}, ${g2 * 255}, ${b2 * 255})`,
		])
	}
}

export function getSatBackground(
	color: ColorSwatches | [number, number, number],
	range?: number,
	minLum?: number,
) {
	if (typeof color === "string") {
		const hsl = swatchesValueMapHSL[color]

		if (!range) {
			return linearGradient([
				`hsl(${hsl[0]}, ${0}%, ${hsl[2]}%)`,
				`hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`,
			])
		} else {
			return linearGradient([
				`hsl(${hsl[0]}, ${Math.max(hsl[1] - range, 0)}%, ${hsl[2]}%)`,
				`hsl(${hsl[0]}, ${Math.min(hsl[1] + range, 100)}%, ${hsl[2]}%)`,
			])
		}
	} else {
		const [hue, sat, val] = color
		const valToDisplay = Math.max(val, minLum ?? 0)
		const [r1, g1, b1] = HSVToRGB([hue, Math.max(sat - (range ?? 40), 5), valToDisplay])
		const [r2, g2, b2] = HSVToRGB([hue, Math.min(sat + (range ?? 40), 100), valToDisplay])

		return linearGradient([
			`rgb(${r1 * 255}, ${g1 * 255}, ${b1 * 255})`,
			`rgb(${r2 * 255}, ${g2 * 255}, ${b2 * 255})`,
		])
	}
}

export function getLumBackground(
	color: ColorSwatches | [number, number, number],
	range: number = 30,
) {
	if (typeof color === "string") {
		const hsl = swatchesValueMapHSL[color]

		return linearGradient([
			`hsl(${hsl[0]}, ${hsl[1]}%, ${Math.max(hsl[2] - range, 5)}%)`,
			`hsl(${hsl[0]}, ${hsl[1]}%, ${Math.min(hsl[2] + range, 100)}%)`,
		])
	} else {
		const [hue, sat, val] = color
		const [r1, g1, b1] = HSVToRGB([hue, sat, Math.max(val - range, 5)])
		const [r2, g2, b2] = HSVToRGB([hue, sat, Math.min(val + range, 100)])

		return linearGradient([
			`rgb(${r1 * 255}, ${g1 * 255}, ${b1 * 255})`,
			`rgb(${r2 * 255}, ${g2 * 255}, ${b2 * 255})`,
		])
	}
}

/* Point Color HSL */

export function shiftRGB(
	[r, g, b]: [number, number, number],
	{ satShift, hueShift, lumShift }: PointColorHSL,
): [number, number, number] {
	const [h, s, v] = RGBToHSV([r, g, b])

	let hue = h + hueShift * PointColorHueMaxShift
	hue = (hue + 360) % 360
	let sat = s + satShift * PointColorSatMaxShift
	sat = Math.min(Math.max(sat, 15), 100)

	const rgb = HSVToRGB([hue, sat, v])

	// roughly consistent with shader for deltaV
	const maxMulti = 105.0 / Math.max(v, 1)
	const factor = Math.min(Math.pow(2, lumShift * 1.1), maxMulti)
	return rgb.map((val) => Math.min(val * factor, 1)) as [number, number, number]
}

/* Utils */

export const getHueShiftRange = (hsl: [number, number, number]): [number, number] => {
	const start = hsl[0] - PointColorHueMaxShift
	const end = hsl[0] + PointColorHueMaxShift

	return [start, end]
}

export const getSatShiftRange = (hsl: [number, number, number]): [number, number] => {
	const start = Math.max(5, hsl[1] - PointColorSatMaxShift)
	const end = Math.min(100, hsl[1] + PointColorSatMaxShift)

	return [start, end]
}

export const getLumShiftRange = (hsl: [number, number, number]): [number, number] => {
	const start = Math.max(5, hsl[2] - PointColorLumMaxShift)
	const end = Math.min(100, hsl[2] + PointColorLumMaxShift)

	return [start, end]
}

export const getDefaultRelativeShiftRange = (value: number): [number, number, number, number] => {
	return [0.0, Math.max(value - 0.17, 0.0), Math.min(value + 0.17, 1.0), 1.0]
}

export const getHSVRelativeShiftInRange = (
	hsv: [number, number, number],
): [number, number, number] => {
	const hueRange = getHueShiftRange(hsv)
	const satRange = getSatShiftRange(hsv)
	const lumRange = getLumShiftRange(hsv)

	return [
		(hsv[0] - hueRange[0]) / (hueRange[1] - hueRange[0]),
		(hsv[1] - satRange[0]) / (satRange[1] - satRange[0]),
		(hsv[2] - lumRange[0]) / (lumRange[1] - lumRange[0]),
	]
}
